<template>
  <v-dialog :value="value" @input="$emit('input', $event)" :fullscreen="$vuetify.breakpoint.smAndDown" max-width="700" scrollable>
    <v-card v-if="product" dark class="product-dialog-card">
      <!-- Schliessen-Button -->
      <v-btn icon class="close-btn" @click="$emit('input', false)">
        <v-icon>mdi-close</v-icon>
      </v-btn>

      <!-- Bilderkarussell -->
      <div class="image-carousel-wrap">
        <v-carousel
          v-if="imageUrls.length > 0"
          height="380"
          hide-delimiter-background
          :show-arrows="imageUrls.length > 1"
          :hide-delimiters="imageUrls.length <= 1"
          dark>
          <v-carousel-item v-for="(url, idx) in imageUrls" :key="idx">
            <div class="carousel-image-wrap">
              <img :src="url" :alt="product.title" class="carousel-image" />
            </div>
          </v-carousel-item>
        </v-carousel>
        <div v-else class="no-image-placeholder">
          <v-icon size="80" color="grey darken-1">mdi-image-off</v-icon>
        </div>
      </div>

      <v-card-text class="pa-6">
        <h2 class="product-title">{{ product.title }}</h2>

        <div class="product-quantity-info caption grey--text mt-1">Menge: {{ product.minQuantity }} – {{ product.maxQuantity }} Stk</div>

        <div v-if="product.description" class="product-description mt-4">
          {{ product.description }}
        </div>

        <v-divider class="my-6" dark></v-divider>

        <!-- Mengen-Auswahl -->
        <div class="qty-selector">
          <div class="qty-label">Anzahl</div>
          <div class="qty-stepper-large">
            <v-btn icon @click="decreaseQty" :disabled="qty <= product.minQuantity">
              <v-icon>mdi-minus</v-icon>
            </v-btn>
            <input
              v-model.number="qty"
              type="number"
              class="qty-input-large"
              :min="product.minQuantity"
              :max="product.maxQuantity"
              @blur="clampQty" />
            <v-btn icon @click="increaseQty" :disabled="qty >= product.maxQuantity">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </div>
        </div>

        <div v-if="cartQuantity > 0" class="caption mt-3" style="color: #009640">
          <v-icon small color="success">mdi-cart-check</v-icon>
          Du hast bereits {{ cartQuantity }} Stk im Warenkorb
        </div>
      </v-card-text>

      <v-divider dark></v-divider>

      <v-card-actions class="pa-4">
        <v-btn text @click="$emit('input', false)">Schliessen</v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" depressed rounded large @click="addToCart">
          <v-icon left>mdi-cart-plus</v-icon>
          {{ qty }} zum Warenkorb
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/api"

export default {
  name: "ProductDetailDialog",
  props: {
    value: { type: Boolean, default: false },
    product: { type: Object, default: null },
    cartQuantity: { type: Number, default: 0 },
  },
  data() {
    return {
      qty: 1,
      imageUrls: [],
    }
  },
  watch: {
    value(open) {
      if (open && this.product) {
        this.qty = this.product.minQuantity || 1
        this.loadImages()
      } else if (!open) {
        this.cleanupImages()
      }
    },
  },
  methods: {
    async loadImages() {
      this.cleanupImages()
      if (!this.product || !this.product.images) return
      const urls = []
      for (const img of this.product.images) {
        try {
          const url = await api.marketingShop.loadProductImage(this.product._id, img.fileId)
          urls.push(url)
        } catch (e) {
          console.error("Bild konnte nicht geladen werden:", e)
        }
      }
      this.imageUrls = urls
    },
    cleanupImages() {
      this.imageUrls.forEach((u) => URL.revokeObjectURL(u))
      this.imageUrls = []
    },
    increaseQty() {
      this.qty = Math.min((this.qty || 1) + 1, this.product.maxQuantity || 100)
    },
    decreaseQty() {
      this.qty = Math.max((this.qty || 1) - 1, this.product.minQuantity || 1)
    },
    clampQty() {
      const min = this.product.minQuantity || 1
      const max = this.product.maxQuantity || 100
      let v = parseInt(this.qty) || min
      v = Math.max(min, Math.min(max, v))
      this.qty = v
    },
    addToCart() {
      this.clampQty()
      this.$emit("add-to-cart", { product: this.product, quantity: this.qty })
      this.$emit("input", false)
    },
  },
  beforeDestroy() {
    this.cleanupImages()
  },
}
</script>

<style scoped>
.product-dialog-card {
}

.close-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  background: rgba(0, 0, 0, 0.5) !important;
}

.image-carousel-wrap {
  background: rgba(255, 255, 255, 0.03);
}

.carousel-image-wrap {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
}

.carousel-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.no-image-placeholder {
  height: 380px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-title {
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
  margin: 0;
}

.product-description {
  font-size: 14px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  white-space: pre-wrap;
}

.qty-selector {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.qty-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}

.qty-stepper-large {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 999px;
  padding: 4px;
}

.qty-input-large {
  width: 60px;
  text-align: center;
  background: transparent;
  border: none;
  color: #fff;
  font-weight: 700;
  font-size: 18px;
  outline: none;
  -moz-appearance: textfield;
}

.qty-input-large::-webkit-outer-spin-button,
.qty-input-large::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
